import clsx from 'clsx';
import { getSeatDisplayText } from 'src/components/common/SeatingInfo/SeatingInfo';
import { Content } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { Tooltip } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature } from 'src/WebApiController';

import * as styles from './ComparableListingSeatingDisplay.css';

export type ComparableListingSeatingDisplayProps = {
  section: string;
  row: string | null;
  seatFr?: string | null;
  seatTo?: string | null;
  isYourListing: boolean;
  hideSectionTitle?: boolean;
};

export const ComparableListingSeatingDisplay = ({
  section,
  row,
  seatFr,
  seatTo,
  isYourListing,
  hideSectionTitle = false,
}: ComparableListingSeatingDisplayProps) => {
  const { isDarkMode } = useSiteTheme();

  const hasSeats = !!(seatFr || seatTo);
  const hasSeatsFeature = useUserHasFeature(Feature.CompListingsSeatNumbers);
  const isSingleSeat = hasSeats && (seatFr === seatTo || !seatTo);
  const seatDisplayText = hasSeats
    ? getSeatDisplayText({ seatFr, seatTo, compact: true })
    : null;

  const content = (
    <div
      className={clsx(styles.seatingText, {
        [styles.seatingTextHighlighted]: !isDarkMode && isYourListing,
        [styles.seatingTextHighlightedDark]: isDarkMode && isYourListing,
      })}
    >
      <span>
        {section}
        {(row || (hasSeatsFeature && hasSeats)) && <span>, </span>}
      </span>

      {row && (
        <span>
          <span
            className={clsx(styles.attrLbl, {
              [styles.attrLblHighlighted]: isYourListing,
              [styles.attrLblHighlightedDark]: isDarkMode && isYourListing,
            })}
          >
            <Content id={ContentId.Row} />
          </span>{' '}
          {row}
          {(hasSeatsFeature && hasSeats) && <span>, </span>}
        </span>
      )}

      {(hasSeatsFeature && hasSeats) && (
        <span>
          <span
            className={clsx(styles.attrLbl, {
              [styles.attrLblHighlighted]: isYourListing,
              [styles.attrLblHighlightedDark]: isDarkMode && isYourListing,
            })}
          >
            <Content id={isSingleSeat ? ContentId.Seat : ContentId.Seats} />
          </span>{' '}
          {seatDisplayText}
        </span>
      )}
    </div>
  );

  return (
    <Tooltip
      triggerContent={content}
      contentVariant={isDarkMode ? 'light' : 'dark'}
    >
      <span>
        {section}
        {(row || (hasSeatsFeature && hasSeats)) && <span>, </span>}
        {row && (
          <span>
            <Content id={ContentId.Row} /> {row}
            {(hasSeatsFeature && hasSeats) && <span>, </span>}
          </span>
        )}
        {(hasSeatsFeature && hasSeats) && (
          <span>
            <Content id={isSingleSeat ? ContentId.Seat : ContentId.Seats} />{' '}
            {seatDisplayText}
          </span>
        )}
      </span>
    </Tooltip>
  );
};
